import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Text,
} from "@radix-ui/themes";
import { Gift } from "lucide-react";
import toast from "react-hot-toast";
import { tenantService } from "@/services/breeze/TenantService";
import { useAuth } from "@/providers/AuthProvider";
import CheckboxField from "@/components/form/CheckboxField";
import InputField from "@/components/form/InputField";
import { UpdateTenantRequest } from "@/types/breeze/Tenant";
import { LoadingScreen } from "@/components/ui/LoadingScreen";

type FormShape = {
  app_engagement_trial_enabled: boolean;
  app_engagement_trial_days: number;
};

export default function EngagementTrial() {
  const queryClient = useQueryClient();
  const { currentTenant, isTenantAdmin } = useAuth();

  // AuthProvider already holds the full tenant including tenant_settings — seed from it so the
  // tab renders instantly. The query still runs in the background to pick up anything updated
  // elsewhere, but the user never sees a spinner.
  const { data: tenant, isLoading } = useQuery({
    queryKey: ["tenant", currentTenant?.id],
    queryFn: () => tenantService.getTenantById(currentTenant!.id),
    enabled: !!currentTenant?.id && isTenantAdmin,
    initialData: currentTenant ?? undefined,
    staleTime: 60_000,
  });

  const { mutate: updateTenant, isPending } = useMutation({
    mutationFn: (data: UpdateTenantRequest) =>
      tenantService.updateTenant(currentTenant!.id, data),
    onSuccess: () => {
      toast.success("Engagement saved");
      queryClient.invalidateQueries({ queryKey: ["tenant", currentTenant?.id] });
    },
  });

  if (!isTenantAdmin) {
    return (
      <Text size="2" color="gray">
        Only project administrators can configure engagement.
      </Text>
    );
  }

  if (isLoading || !tenant) return <LoadingScreen fullPage={false} />;

  const initialValues: FormShape = {
    app_engagement_trial_enabled:
      tenant.tenant_settings?.app_engagement_trial_enabled || false,
    app_engagement_trial_days:
      tenant.tenant_settings?.app_engagement_trial_days ?? 7,
  };

  return (
    <Flex direction="column" gap="4">
      <Card size="3">
        <Box p="1">
          <Flex align="center" gap="2" mb="1">
            <Gift size={18} />
            <Heading size="4" weight="medium">Engagement</Heading>
          </Flex>
          <Text size="2" color="gray" as="p" mb="4">
            Give every new user a free window of app access before they need to
            subscribe. The trial runs once per user and applies regardless of
            payment processor — even projects without AnythinkPay configured can
            use it.
          </Text>

          <Formik<FormShape>
            enableReinitialize
            initialValues={initialValues}
            onSubmit={(values) => {
              updateTenant({
                name: tenant.name,
                tenant_settings: {
                  ...tenant.tenant_settings,
                  // Preserve the configured length even if the toggle is off, so toggling back on
                  // doesn't lose the previously-set number.
                  app_engagement_trial_enabled: values.app_engagement_trial_enabled,
                  app_engagement_trial_days:
                    Number(values.app_engagement_trial_days) || 0,
                } as UpdateTenantRequest["tenant_settings"],
              } as UpdateTenantRequest);
            }}
          >
            {({ values }) => (
              <Form>
                <Flex direction="column" gap="4">
                  <CheckboxField
                    name="app_engagement_trial_enabled"
                    label="Offer a free trial to users who haven't subscribed"
                    description="When on, a signed-up user gets free access for the length below. Each user can take it once."
                  />
                  {values.app_engagement_trial_enabled && (
                    <Box style={{ maxWidth: 240 }}>
                      <InputField
                        name="app_engagement_trial_days"
                        label="Trial length (days)"
                        type="number"
                        required
                        min={1}
                        max={365}
                        step={1}
                        description="Days of free access before the subscription gate kicks in."
                      />
                    </Box>
                  )}
                </Flex>
                <Flex justify="end" mt="5">
                  <Button type="submit" loading={isPending}>
                    Save
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </Box>
      </Card>

    </Flex>
  );
}
